
import { computed } from "vue";

import type { Schueler } from "@core/asd/data/schueler/Schueler";
import type { GostBlockungKurs } from "@core/core/data/gost/GostBlockungKurs";
import type { GostBlockungKursLehrer } from "@core/core/data/gost/GostBlockungKursLehrer";
import type { GostBlockungListeneintrag } from "@core/core/data/gost/GostBlockungListeneintrag";
import type { GostBlockungRegelUpdate } from "@core/core/data/gost/GostBlockungRegelUpdate";
import type { GostBlockungSchiene } from "@core/core/data/gost/GostBlockungSchiene";
import { GostBlockungsdaten } from "@core/core/data/gost/GostBlockungsdaten";
import { GostBlockungsergebnis } from "@core/core/data/gost/GostBlockungsergebnis";
import type { GostBlockungsergebnisKurs } from "@core/core/data/gost/GostBlockungsergebnisKurs";
import type { GostBlockungsergebnisKursSchuelerZuordnungUpdate } from "@core/core/data/gost/GostBlockungsergebnisKursSchuelerZuordnungUpdate";
import type { GostJahrgangsdaten } from "@core/core/data/gost/GostJahrgangsdaten";
import type { GostSchuelerWiederholung } from "@core/core/data/gost/GostSchuelerWiederholung";
import type { GostStatistikFachwahl } from "@core/core/data/gost/GostStatistikFachwahl";
import type { LehrerListeEintrag } from "@core/core/data/lehrer/LehrerListeEintrag";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { GostHalbjahr } from "@core/core/types/gost/GostHalbjahr";
import { GostBlockungsdatenManager } from "@core/core/utils/gost/GostBlockungsdatenManager";
import { GostBlockungsergebnisManager } from "@core/core/utils/gost/GostBlockungsergebnisManager";
import { GostFaecherManager } from "@core/core/utils/gost/GostFaecherManager";
import { ArrayList } from "@core/java/util/ArrayList";
import { HashSet } from "@core/java/util/HashSet";
import type { JavaSet } from "@core/java/util/JavaSet";
import type { List } from "@core/java/util/List";

import { GostKursplanungSchuelerFilter } from "~/components/gost/kursplanung/GostKursplanungSchuelerFilter";
import { api } from "~/router/Api";
import { routeGostKursplanung } from "~/router/apps/gost/kursplanung/RouteGostKursplanung";
import { routeGostKursplanungSchueler } from "~/router/apps/gost/kursplanung/RouteGostKursplanungSchueler";
import { RouteData, type RouteStateInterface } from "~/router/RouteData";
import { RouteManager } from "~/router/RouteManager";
import { abschnittStateImpl } from "~/states/AbschnittStateImpl";
import { configStateImpl } from "~/states/ConfigStateImpl";
import { serverStateImpl } from "~/states/ServerStateImpl";

type BlockungstabelleStates = 'nichts' | 'alles' | 'schienen';

interface RouteStateGostKursplanung extends RouteStateInterface {
	// Daten nur abhängig von dem Abiturjahrgang
	abiturjahr: number | undefined;
	jahrgangsdaten: GostJahrgangsdaten | undefined;
	faecherManager: GostFaecherManager;
	mapFachwahlStatistik: Map<number, GostStatistikFachwahl>;
	// ... die mit dem Abiturjahrgang aktualisiert/mitgeladen werden
	mapWiederholungen: Map<number, GostSchuelerWiederholung>;
	mapLehrer: Map<number, LehrerListeEintrag>;
	// ... auch abhängig vom ausgewählten Halbjahr der gymnasialen Oberstufe
	halbjahr: GostHalbjahr;
	halbjahrInitialisiert: boolean;
	mapBlockungen: Map<number, GostBlockungListeneintrag>;
	existiertSchuljahresabschnitt: boolean;
	// ...auch abhängig von der ausgewählten Blockung
	auswahlBlockung: GostBlockungListeneintrag | undefined;
	datenmanager: GostBlockungsdatenManager | undefined;
	// ...auch abhängig von dem ausgewählten Blockungsergebnis
	auswahlErgebnis: GostBlockungsergebnis | undefined;
	ergebnismanager: GostBlockungsergebnisManager | undefined;
	schuelerFilter: GostKursplanungSchuelerFilter | undefined;
	// ... auch abhängig von dem ausgewählten Schüler
	auswahlSchueler: Schueler | undefined;
	kursauswahl: JavaSet<number>;
}

const defaultState: RouteStateGostKursplanung = {
	abiturjahr: undefined,
	jahrgangsdaten: undefined,
	faecherManager: new GostFaecherManager(-1),
	mapFachwahlStatistik: new Map(),
	mapWiederholungen: new Map(),
	mapLehrer: new Map(),
	halbjahr: GostHalbjahr.EF1,
	halbjahrInitialisiert: false,
	mapBlockungen: new Map(),
	existiertSchuljahresabschnitt: false,
	auswahlBlockung: undefined,
	datenmanager: undefined,
	auswahlErgebnis: undefined,
	ergebnismanager: undefined,
	schuelerFilter: undefined,
	auswahlSchueler: undefined,
	kursauswahl: new HashSet<number>(),
};

export class RouteDataGostKursplanung extends RouteData<RouteStateGostKursplanung> {

	public constructor() {
		super(defaultState);
	}

	public get hatAbiturjahr(): boolean {
		return this._state.value.abiturjahr !== undefined;
	}

	public get abiturjahrIstVorlage(): boolean {
		return (this._state.value.abiturjahr !== undefined) && (this._state.value.abiturjahr === -1);
	}

	public get abiturjahr(): number {
		if (this._state.value.abiturjahr === undefined) {
			throw new DeveloperNotificationException("Es wurde noch kein Abiturjahrgang geladen.");
		}
		return this._state.value.abiturjahr;
	}

	public setAbiturjahr = async (abiturjahr: number | undefined, force: boolean): Promise<boolean> => {
		const abiturjahrwechsel = (abiturjahr !== this._state.value.abiturjahr);
		if (!abiturjahrwechsel && (!force)) {
			return false;
		}
		if (abiturjahr === undefined) {
			this.setDefaultState();
			return true;
		}
		api.status.start();
		// Lade die Daten für die Kursplanung, die nur vom Abiturjahrgang abhängen
		const jahrgangsdaten = await api.server.getGostAbiturjahrgang(api.schema, abiturjahr);
		const listFaecher = await api.server.getGostAbiturjahrgangFaecher(api.schema, abiturjahr);
		const faecherManager = new GostFaecherManager(abiturjahr - 1, listFaecher);
		// Lade die Lehrerliste
		const listLehrer = await api.server.getLehrer(api.schema);
		const mapLehrer: Map<number, LehrerListeEintrag> = new Map();
		for (const l of listLehrer) {
			mapLehrer.set(l.id, l);
		}
		// Lade die Fachwahlstatistik
		const listFachwahlStatistik = await api.server.getGostAbiturjahrgangFachwahlstatistik(api.schema, abiturjahr);
		const mapFachwahlStatistik: Map<number, GostStatistikFachwahl> = new Map();
		for (const fw of listFachwahlStatistik) {
			mapFachwahlStatistik.set(fw.id, fw);
		}
		// Lade die Wiederholungen, damit Schüler mit einem Wechsel des Abiturjahrgangs gekennzeichnet werden können. Sie gibt es zunächst
		// nur im Servermode DEV, sonst lehnt der Server die Abfrage ab.
		const listWiederholungen = serverStateImpl.hasDev ? await api.server.getGostSchuelerWiederholungen(api.schema) : [];
		const mapWiederholungen = new Map<number, GostSchuelerWiederholung>();
		for (const v of listWiederholungen) {
			mapWiederholungen.set(v.idSchueler, v);
		}
		api.status.stop();
		// Setze den State neu
		this.setPatchedDefaultState({
			abiturjahr,
			jahrgangsdaten,
			faecherManager,
			mapLehrer,
			halbjahr: this._state.value.halbjahr,
			halbjahrInitialisiert: false,
			mapFachwahlStatistik,
			mapWiederholungen,
		});
		return abiturjahrwechsel;
	};

	public get jahrgangsdaten(): GostJahrgangsdaten {
		if (this._state.value.jahrgangsdaten === undefined) {
			throw new DeveloperNotificationException("Es wurde noch kein Abiturjahrgang geladen, so dass keine Jahrgangsdaten zur Verfügung stehen.");
		}
		return this._state.value.jahrgangsdaten;
	}

	public get faecherManager(): GostFaecherManager {
		return this._state.value.faecherManager;
	}

	public get mapFachwahlStatistik(): Map<number, GostStatistikFachwahl> {
		return this._state.value.mapFachwahlStatistik;
	}

	public get mapWiederholungen(): Map<number, GostSchuelerWiederholung> {
		return this._state.value.mapWiederholungen;
	}

	public get mapLehrer(): Map<number, LehrerListeEintrag> {
		return this._state.value.mapLehrer;
	}

	public get halbjahr(): GostHalbjahr {
		return this._state.value.halbjahr;
	}

	get zuletztBesucht(): Map<number, { halbjahrId: number, idBlockung: number, idErgebnis: number }> {
		const text = configStateImpl.config.getValue("gost.kursplanung.route.zuletztBesucht");
		const arr = JSON.parse(text);
		return new Map(arr);
	}

	setZuletztBesucht = async (value: Map<number, { halbjahrId: number, idBlockung: number, idErgebnis: number }>) => {
		const text = JSON.stringify([...value.entries()]);
		await configStateImpl.config.setValue('gost.kursplanung.route.zuletztBesucht', text);
	};

	get blockungstabelleHidden(): BlockungstabelleStates {
		return configStateImpl.config.getValue("gost.kursplanung.kursansicht.ausgeblendet") as BlockungstabelleStates;
	}

	setBlockungstabelleHidden = async (value: BlockungstabelleStates) => {
		await configStateImpl.config.setValue('gost.kursplanung.kursansicht.ausgeblendet', value);
	};

	get zeigeSchienenbezeichnungen(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.kursansicht.zeigeSchienenbezeichnung") === 'true';
	}

	setZeigeSchienenbezeichnungen = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.kursansicht.zeigeSchienenbezeichnung', value ? "true" : "false");
	};

	get isSchuelerFilterOpen(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.schueler.auswahl.filterOpen") === 'true';
	}

	setIsSchuelerFilterOpen = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.schueler.auswahl.filterOpen', value ? "true" : "false");
	};

	get showGeschlecht(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.schueler.auswahl.geschlecht") === 'true';
	}

	setShowGeschlecht = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.schueler.auswahl.geschlecht', value ? "true" : "false");
	};

	get fixierteVerschieben(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.umkursen.fixierteVerschieben") === 'true';
	}

	setFixierteVerschieben = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.umkursen.fixierteVerschieben', value ? "true" : "false");
	};

	get inZielkursFixieren(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.umkursen.inZielkursFixieren") === 'true';
	}

	setInZielkursFixieren = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.umkursen.inZielkursFixieren', value ? "true" : "false");
	};

	get ausfuehrlicheDarstellungKursdifferenz(): boolean {
		return configStateImpl.config.getValue("gost.kursplanung.berechnung.ausfuehrlicheDarstellungKursdifferenz") === 'true';
	}

	setAusfuehrlicheDarstellungKursdifferenz = async (value: boolean) => {
		await configStateImpl.config.setValue('gost.kursplanung.berechnung.ausfuehrlicheDarstellungKursdifferenz', value ? "true" : "false");
	};

	public setHalbjahr = async (halbjahr: GostHalbjahr): Promise<boolean> => {
		if (this._state.value.abiturjahr === undefined) {
			throw new DeveloperNotificationException("Es kann kein Halbjahr ausgewählt werden, wenn zuvor kein Abiturjahrgang ausgewählt wurde.");
		}
		const result: boolean = (halbjahr !== this._state.value.halbjahr);
		if ((!result) && (this._state.value.halbjahrInitialisiert === true)) {
			return result;
		}
		// Lade die Liste der Blockungen
		api.status.start();
		const listBlockungen = await api.server.getGostAbiturjahrgangBlockungsliste(api.schema, this.abiturjahr, halbjahr.id);
		const mapBlockungen: Map<number, GostBlockungListeneintrag> = new Map();
		for (const bl of listBlockungen) {
			mapBlockungen.set(bl.id, bl);
		}
		let auswahlBlockung: GostBlockungListeneintrag | undefined = undefined;
		if (listBlockungen.size() > 0) {
			for (const bl of listBlockungen) {
				if (bl.istAktiv === true) {
					auswahlBlockung = bl;
					break;
				}
			}
			auswahlBlockung ??= listBlockungen.get(0);
		}
		const schuljahr = halbjahr.getSchuljahrFromAbiturjahr(this._state.value.abiturjahr);
		const abschnitt = abschnittStateImpl.getBySchuljahrUndHalbjahr(schuljahr, halbjahr.halbjahr);
		const existiertSchuljahresabschnitt = (abschnitt !== null);
		api.status.stop();
		this.setPatchedState({
			halbjahr,
			halbjahrInitialisiert: true,
			mapBlockungen,
			existiertSchuljahresabschnitt,
			auswahlBlockung,
			datenmanager: undefined,
			auswahlErgebnis: undefined,
			ergebnismanager: undefined,
			schuelerFilter: undefined,
			kursauswahl: new HashSet<number>(),
		});
		return result;
	};

	public unsetHalbjahr() {
		this.setPatchedState({
			halbjahrInitialisiert: false,
			mapBlockungen: new Map(),
			existiertSchuljahresabschnitt: false,
			auswahlBlockung: undefined,
			datenmanager: undefined,
			auswahlErgebnis: undefined,
			ergebnismanager: undefined,
			schuelerFilter: undefined,
		});
	}

	public get existiertSchuljahresabschnitt(): boolean {
		return this._state.value.existiertSchuljahresabschnitt;
	}

	/**
	 * Gibt zurück, ob eine Blockung, also ein Datenmanager existiert. Es wird
	 * direkt auf dem State geprüft, da der Getter für den Datenmanager bei
	 * undefined einen Fehler erzeugen würde.
	 */
	public get hatBlockung(): boolean {
		return this._state.value.datenmanager !== undefined;
	}

	public get mapBlockungen(): Map<number, GostBlockungListeneintrag> {
		return this._state.value.mapBlockungen;
	}

	public get auswahlBlockung(): GostBlockungListeneintrag {
		if (this._state.value.auswahlBlockung === undefined) {
			throw new DeveloperNotificationException("Es wurde noch keine gültige Blockung ausgewählt.");
		}
		return this._state.value.auswahlBlockung;
	}

	public setAuswahlBlockung = async (value: GostBlockungListeneintrag | undefined, force?: boolean) => {
		if (this._state.value.abiturjahr === undefined) {
			throw new DeveloperNotificationException("Es kann keine Blockung ausgewählt werden, wenn zuvor kein Abiturjahrgang ausgewählt wurde.");
		}
		if ((force !== true) && (this._state.value.auswahlBlockung?.id === value?.id) && (this._state.value.datenmanager !== undefined)) {
			return;
		}
		if (value === undefined) {
			this.setPatchedState({
				auswahlBlockung: undefined,
				datenmanager: undefined,
				auswahlErgebnis: undefined,
				ergebnismanager: undefined,
				schuelerFilter: undefined,
				auswahlSchueler: undefined,
				kursauswahl: new HashSet<number>(),
			});
			return;
		}
		api.status.start();
		const blockungsdatenGzip = await api.server.getGostBlockungGZip(api.schema, value.id);
		const blockungsdatenBlob = await new Response(blockungsdatenGzip.data.stream().pipeThrough(new DecompressionStream("gzip"))).blob();
		const blockungsdaten = GostBlockungsdaten.transpilerFromJSON(await blockungsdatenBlob.text());
		const datenmanager = new GostBlockungsdatenManager(blockungsdaten, this.faecherManager);
		const ergebnisse = datenmanager.ergebnisGetListeSortiertNachBewertung();
		api.status.stop();
		this.setPatchedState({
			auswahlBlockung: value,
			datenmanager,
			auswahlErgebnis: undefined,
			ergebnismanager: undefined,
			schuelerFilter: undefined,
			kursauswahl: new HashSet<number>(),
		});
		let ergebnis: GostBlockungsergebnis | undefined = undefined;
		if (ergebnisse.size() > 0) {
			for (const e of ergebnisse) {
				if (e.istAktiv) {
					ergebnis = e;
					break;
				}
			}
			ergebnis ??= ergebnisse.get(0);
		}
		await this.setAuswahlErgebnis(ergebnis);
	};

	public get datenmanager(): GostBlockungsdatenManager {
		// Wurde bereits eine Blockung geladen, so dass kein Daten-Manager zur Verfügung steht?
		// Wenn nicht, dann übergebe einen leeren Daten-Manager.
		if (this._state.value.datenmanager === undefined) {
			return new GostBlockungsdatenManager(new GostBlockungsdaten(), new GostFaecherManager(-1));
		}
		return this._state.value.datenmanager;
	}

	public get kursAuswahl(): JavaSet<number> {
		return this._state.value.kursauswahl;
	}

	public get ergebnismanager(): GostBlockungsergebnisManager {
		// Prüfe, ob bereits ein Ergebnis ausgewählt wurde
		// Wenn nicht, dann erstelle einen leeren Ergebnis-Manager
		if (this._state.value.ergebnismanager === undefined) {
			return new GostBlockungsergebnisManager(this.datenmanager, -1);
		}
		return this._state.value.ergebnismanager;
	}

	/**
	 * Gibt zurück, ob ein Blockungsergebnis, also ein Ergebnismanager existiert. Es wird
	 * direkt auf dem State geprüft, da der Getter für den Ergebnismanager bei
	 * undefined einen Fehler erzeugen würde.
	 */
	public get hatErgebnis(): boolean {
		return this._state.value.ergebnismanager !== undefined;
	}

	public get ergebnisse(): List<GostBlockungsergebnis> {
		return this.datenmanager.ergebnisGetListeSortiertNachBewertung();
	}

	public get auswahlErgebnis(): GostBlockungsergebnis {
		if (this._state.value.auswahlErgebnis === undefined) {
			throw new DeveloperNotificationException("Es wurde noch kein gültiges Ergebnis ausgewählt.");
		}
		return this._state.value.auswahlErgebnis;
	}

	public setAuswahlErgebnis = async (auswahlErgebnis: GostBlockungsergebnis | undefined) => {
		if (this._state.value.abiturjahr === undefined) {
			throw new DeveloperNotificationException("Es kann kein Ergebnis ausgewählt werden, wenn zuvor kein Abiturjahrgang ausgewählt wurde.");
		}
		if ((this._state.value.auswahlErgebnis?.id === auswahlErgebnis?.id) && (this._state.value.ergebnismanager !== undefined)) {
			return;
		}
		if (auswahlErgebnis === undefined) {
			this.setPatchedState({
				auswahlErgebnis: undefined,
				ergebnismanager: undefined,
				schuelerFilter: undefined,
				auswahlSchueler: undefined,
			});
			return;
		}
		if (this._state.value.datenmanager === undefined) {
			throw new DeveloperNotificationException("Es kann kein Ergebnis ausgewählt werden, wenn zuvor keine Blockung ausgewählt wurde.");
		}
		api.status.start();
		const ergebnismanager = this.datenmanager.ergebnisManagerGet(auswahlErgebnis.id);
		const schuelerFilter = new GostKursplanungSchuelerFilter(this.datenmanager, () => this.ergebnismanager, this.faecherManager.faecher());
		api.status.stop();
		this.setPatchedState({ auswahlErgebnis, ergebnismanager, schuelerFilter });
	};

	public get schuelerFilter(): GostKursplanungSchuelerFilter {
		const filter = this._state.value.schuelerFilter;
		if (filter === undefined) {
			throw new DeveloperNotificationException("Es wurde noch kein Schüler-Filter erstellt.");
		}
		return filter;
	}

	public get hatSchueler(): boolean {
		return this._state.value.auswahlSchueler !== undefined;
	}

	public get auswahlSchueler(): Schueler {
		if (this._state.value.auswahlSchueler === undefined) {
			throw new DeveloperNotificationException("Es wurde noch kein Schüler ausgewählt.");
		}
		return this._state.value.auswahlSchueler;
	}

	public async setAuswahlSchueler(auswahlSchueler: Schueler | undefined) {
		if (this._state.value.abiturjahr === undefined) {
			throw new DeveloperNotificationException("Es kann kein Ergebnis ausgewählt werden, wenn zuvor kein Abiturjahrgang ausgewählt wurde.");
		}
		if (auswahlSchueler?.id === this._state.value.auswahlSchueler?.id) {
			return;
		}
		// Setze die neue Schülerauswahl im geklonten State
		this.setPatchedState({ auswahlSchueler });
	}

	addBlockung = async () => {
		await api.call(async () => {
			if ((this._state.value.abiturjahr === undefined) || (this._state.value.abiturjahr === -1)) {
				return;
			}
			const result = await api.server.createGostAbiturjahrgangBlockung(api.schema, this.jahrgangsdaten.abiturjahr, this.halbjahr.id);
			this.mapBlockungen.set(result.id, result);
			this.setPatchedState({ mapBlockungen: this.mapBlockungen });
			await this.gotoBlockung(result);
		})();
	};

	restoreBlockung = async () => {
		await api.call(async () => {
			const result = await api.server.restauriereGostBlockung(api.schema, this.jahrgangsdaten.abiturjahr, this.halbjahr.id);
			this.mapBlockungen.set(result.id, result);
			// Lade die Fächer des Abiturjahrgangs auch neu, da diese eventuell beim Wiederherstellen der Blockung angepasst wurden.
			const listFaecher = await api.server.getGostAbiturjahrgangFaecher(api.schema, this.jahrgangsdaten.abiturjahr);
			const faecherManager = new GostFaecherManager(this.jahrgangsdaten.abiturjahr - 1, listFaecher);
			// Aktualisiere den State
			this.setPatchedState({
				faecherManager,
				mapBlockungen: this.mapBlockungen,
			});
			await this.gotoBlockung(result);
		})();
	};

	removeBlockung = async () => {
		await api.call(async () => {
			if (!this.hatBlockung) {
				return;
			}
			await api.server.deleteGostBlockung(api.schema, this.auswahlBlockung.id);
			this._state.value.mapBlockungen.delete(this.auswahlBlockung.id);
			await this.setAuswahlBlockung(undefined);
			await this.gotoHalbjahr(this.halbjahr);
		})();
	};

	patchBlockung = async (data: Partial<GostBlockungsdaten>, idBlockung: number): Promise<boolean> => {
		if (this._state.value.datenmanager === undefined) {
			throw new DeveloperNotificationException("Es wurde noch keine Blockung geladen, so dass die Blockung nicht angepasst werden kann.");
		}
		api.status.start();
		await api.server.patchGostBlockung(data, api.schema, idBlockung);
		if (data.name !== undefined) {
			this.datenmanager.setName(data.name);
		}
		if (data.istAktiv !== undefined) {
			if (data.istAktiv === true) {
				for (const blockung of this.mapBlockungen.values()) {
					blockung.istAktiv = (blockung.id === idBlockung);
				}
			} else {
				const blockung = this.mapBlockungen.get(idBlockung);
				if (blockung !== undefined) {
					blockung.istAktiv = false;
				}
			}
			this.datenmanager.daten().istAktiv = (this.datenmanager.daten().id === idBlockung);
		}
		api.status.stop();
		this.commit();
		return true;
	};


	patchErgebnis = async (data: Partial<GostBlockungsergebnis>, idErgebnis: number): Promise<boolean> => {
		if (this._state.value.datenmanager === undefined) {
			throw new DeveloperNotificationException("Es wurde noch keine Blockung geladen, so dass die Ergebnisliste nicht angepasst werden kann.");
		}
		api.status.start();
		await api.server.patchGostBlockungsergebnis(data, api.schema, idErgebnis);
		if (data.istAktiv === true) {
			for (const ergebnis of this.datenmanager.ergebnisGetListeSortiertNachBewertung()) {
				ergebnis.istAktiv = (ergebnis.id === idErgebnis);
			}
		} else if (data.istAktiv === false) {
			this.datenmanager.ergebnisGet(idErgebnis).istAktiv = false;
		}
		api.status.stop();
		this.commit();
		return true;
	};

	patchKurs = async (data: Partial<GostBlockungKurs>, kurs_id: number) => {
		await api.call(async (data: Partial<GostBlockungKurs>, kurs_id: number) => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			await api.server.patchGostBlockungKurs(data, api.schema, kurs_id);
			if (data.suffix !== undefined) {
				this.datenmanager.kursSetSuffix(kurs_id, data.suffix);
			}
			if (data.istKoopKurs !== undefined) {
				this.datenmanager.kursGet(kurs_id).istKoopKurs = data.istKoopKurs;
			}
			this.setPatchedState({ datenmanager: this.datenmanager, ergebnismanager: this.ergebnismanager });
		})(data, kurs_id);
	};

	addKurs = async (fach_id: number, kursart_id: number): Promise<GostBlockungKurs | undefined> => {
		return await api.call(async (fach_id: number, kursart_id: number): Promise<GostBlockungKurs | undefined> => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			const kurs = await api.server.addGostBlockungKurs(api.schema, this.auswahlBlockung.id, fach_id, kursart_id);
			this.datenmanager.kursAdd(kurs);
			this.ergebnismanager.setAddKursByID(kurs.id);
			this.commit();
			return kurs;
		})(fach_id, kursart_id);
	};

	removeKurse = async (ids: Iterable<number>) => {
		await api.call(async (ids: Iterable<number>) => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			const list = new ArrayList<number>();
			for (const id of ids) {
				list.add(id);
			}
			// prüfe, ob der Schülerfilter noch eine Referenz zum Kurs hat
			if ((this.schuelerFilter.kurs?.id !== undefined) && list.contains(this.schuelerFilter.kurs.id)) {
				this.schuelerFilter.reset();
			} else if (this.schuelerFilter.fach !== undefined) {
				// Prüfe, ob der Schülerfilter noch eine Referenz zum Fach des Kurses hat
				const listFachIDs = this.ergebnismanager.getOfFachKursmenge(this.schuelerFilter.fach);
				for (const k of list) {
					if (listFachIDs.contains(k)) {
						this.schuelerFilter.reset();
						break;
					}
				}
			}
			await api.server.deleteGostBlockungKurse(list, api.schema);
			this.datenmanager.kurseRemoveByID(list);
			this.ergebnismanager.setRemoveKurseByID(list);
			this.kursAuswahl.removeAll(list);
			this.commit();
		})(ids);
	};

	combineKurs = async (kurs1: GostBlockungKurs, kurs2: GostBlockungKurs | GostBlockungsergebnisKurs | undefined | null) => {
		await api.call(async (kurs1: GostBlockungKurs, kurs2: GostBlockungKurs | GostBlockungsergebnisKurs | undefined | null) => {
			if ((kurs2 === undefined) || (kurs2 === null)) {
				return;
			}
			await api.server.combineGostBlockungKurs(api.schema, kurs1.id, kurs2.id);
			this.ergebnismanager.setMergeKurseByID(kurs1.id, kurs2.id);
			this.commit();
		})(kurs1, kurs2);
	};

	splitKurs = async (kurs: GostBlockungKurs) => {
		await api.call(async (kurs: GostBlockungKurs) => {
			const { kurs1, kurs2, schueler2 } = await api.server.splitGostBlockungKurs(api.schema, kurs.id);
			this.ergebnismanager.setSplitKurs(kurs1, kurs2, <number[]>schueler2.toArray());
			this.commit();
		})(kurs);
	};

	addSchieneKurs = async (kurs: GostBlockungKurs) => {
		await api.call(async (kurs: GostBlockungKurs) => {
			if ((!this.hatBlockung) || (!this.hatErgebnis) || (kurs.anzahlSchienen >= this.datenmanager.schieneGetAnzahl())) {
				return;
			}
			this.ergebnismanager.patchOfKursSchienenAnzahl(kurs.id, kurs.anzahlSchienen + 1);
			const k = this.ergebnismanager.getKursE(kurs.id);
			await this.patchKurs(k, k.id);
		})(kurs);
	};

	removeSchieneKurs = async (kurs: GostBlockungKurs) => {
		await api.call(async (kurs: GostBlockungKurs) => {
			if ((!this.hatBlockung) || (!this.hatErgebnis) || (kurs.anzahlSchienen <= 1)) {
				return;
			}
			this.ergebnismanager.patchOfKursSchienenAnzahl(kurs.id, kurs.anzahlSchienen - 1);
			const k = this.ergebnismanager.getKursE(kurs.id);
			await this.patchKurs(k, k.id);
		})(kurs);
	};

	addKursLehrer = async (kurs_id: number, lehrer_id: number): Promise<GostBlockungKursLehrer | undefined> => {
		return await api.call(async (kurs_id: number, lehrer_id: number): Promise<GostBlockungKursLehrer | undefined> => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			const lehrer = await api.server.addGostBlockungKurslehrer(api.schema, kurs_id, lehrer_id);
			this.datenmanager.kursAddLehrkraft(kurs_id, lehrer);
			this.ergebnismanager.patchOfKursLehrkaefteChanged();
			this.commit();
			return lehrer;
		})(kurs_id, lehrer_id);
	};

	removeKursLehrer = async (kurs_id: number, lehrer_id: number): Promise<void> => {
		await api.call(async (kurs_id: number, lehrer_id: number): Promise<void> => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			await api.server.deleteGostBlockungKurslehrer(api.schema, kurs_id, lehrer_id);
			this.datenmanager.kursRemoveLehrkraft(kurs_id, lehrer_id);
			this.ergebnismanager.patchOfKursLehrkaefteChanged();
			this.commit();
		})(kurs_id, lehrer_id);
	};

	patchSchiene = async (data: Partial<GostBlockungSchiene>, id: number) => {
		await api.call(async (data: Partial<GostBlockungSchiene>, id: number) => {
			await api.server.patchGostBlockungSchiene(data, api.schema, id);
			const bezeichnung = data.bezeichnung;
			if (bezeichnung === undefined) {
				return;
			}
			const datenmanager = this.datenmanager;
			datenmanager.schienePatchBezeichnung(id, bezeichnung);
			this.setPatchedState({ datenmanager });
		})(data, id);
	};

	addSchiene = async (): Promise<GostBlockungSchiene | undefined> => {
		return await api.call(async (): Promise<GostBlockungSchiene | undefined> => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			const schiene = await api.server.addGostBlockungSchiene(api.schema, this.auswahlBlockung.id);
			this.datenmanager.schieneAdd(schiene);
			this.ergebnismanager.setAddSchieneByID(schiene.id);
			this.commit();
			return schiene;
		})();
	};

	removeSchiene = async (schiene: GostBlockungSchiene) => {
		return await api.call(async (schiene: GostBlockungSchiene) => {
			if ((!this.hatBlockung) || (!this.hatErgebnis)) {
				return;
			}
			const result = await api.server.deleteGostBlockungSchieneByID(api.schema, schiene.id);
			this.datenmanager.schieneRemoveByID(result.id);
			this.ergebnismanager.setRemoveSchieneByID(result.id);
			this.commit();
			return result;
		})(schiene);
	};

	updateKursSchienenZuordnung = async (idKurs: number, idSchieneAlt: number, idSchieneNeu: number): Promise<boolean> => {
		return await api.call(async (idKurs: number, idSchieneAlt: number, idSchieneNeu: number): Promise<boolean> => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return false;
			}
			await api.server.updateGostBlockungsergebnisKursSchieneZuordnung(api.schema, this._state.value.auswahlErgebnis.id, idSchieneAlt, idKurs, idSchieneNeu);
			const update = this.ergebnismanager.kursSchienenUpdateVerschiebeKursVonSchieneNachSchiene(idKurs, idSchieneAlt, idSchieneNeu);
			this.ergebnismanager.kursSchienenUpdateExecute(update);
			this.commit();
			return true;
		})(idKurs, idSchieneAlt, idSchieneNeu);
	};

	updateKursSchuelerZuordnungen = async (update: GostBlockungsergebnisKursSchuelerZuordnungUpdate): Promise<boolean> => {
		return await api.call(async (update: GostBlockungsergebnisKursSchuelerZuordnungUpdate): Promise<boolean> => {
			if (update.listEntfernen.isEmpty() && update.listHinzuzufuegen.isEmpty()) {
				return true;
			}
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return false;
			}
			const ergebnisid = this._state.value.auswahlErgebnis.id;
			// Aktualisiere die Zuordnungen ...
			const regelUpdates = await api.server.updateGostBlockungsergebnisKursSchuelerZuordnungen(update, api.schema, ergebnisid);
			update.regelUpdates.listHinzuzufuegen = regelUpdates;
			this.ergebnismanager.kursSchuelerUpdateExecute(update);
			this.commit();
			return true;
		})(update);
	};

	autoKursSchuelerZuordnung = async (idSchueler: number) => {
		await api.call(async (idSchueler: number) => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return;
			}
			const ergebnisid = this._state.value.auswahlErgebnis.id;
			const update = this.ergebnismanager.getOfSchuelerNeuzuordnung(idSchueler, false);
			if (update.listEntfernen.isEmpty() && update.listHinzuzufuegen.isEmpty()) {
				return;
			}
			const regelUpdates = await api.server.updateGostBlockungsergebnisKursSchuelerZuordnungen(update, api.schema, ergebnisid);
			update.regelUpdates.listHinzuzufuegen = regelUpdates;
			this.ergebnismanager.kursSchuelerUpdateExecute(update);
			this.commit();
		})(idSchueler);
	};

	addErgebnisse = async (ergebnisse: List<GostBlockungsergebnis>): Promise<void> => {
		await api.call(async (ergebnisse: List<GostBlockungsergebnis>): Promise<void> => {
			if ((ergebnisse.isEmpty()) || (!this.hatBlockung)) {
				return;
			}
			const idBlockung = this.datenmanager.daten().id;
			const ergebnisseMitIDs = await api.server.addGostBlockungErgebnisse(ergebnisse, api.schema, idBlockung);
			this.datenmanager.ergebnisAddListe(ergebnisseMitIDs);
			this.auswahlBlockung.anzahlErgebnisse = this.datenmanager.ergebnisGetListeSortiertNachBewertung().size();
			this.commit();
		})(ergebnisse);
	};

	removeErgebnisse = async (ergebnisse: Iterable<GostBlockungsergebnis>): Promise<void> => {
		await api.call(async (ergebnisse: Iterable<GostBlockungsergebnis>): Promise<void> => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return;
			}
			const liste = new ArrayList<number>();
			const set = new HashSet<number>();
			const ergebnisid = this._state.value.auswahlErgebnis.id;
			for (const ergebnis of ergebnisse) {
				set.add(ergebnis.id);
				liste.add(ergebnis.id);
			}
			const reselect = liste.contains(ergebnisid);
			await api.server.deleteGostBlockungsergebnisse(liste, api.schema);
			this.datenmanager.ergebnisRemoveListeByIDs(set);
			this.auswahlBlockung.anzahlErgebnisse = this.datenmanager.ergebnisGetListeSortiertNachBewertung().size();
			if (reselect) {
				for (const e of this.ergebnisse) {
					if (!set.contains(e.id)) {
						await this.gotoErgebnis(e);
						break;
					}
				}
			} else {
				this.commit();
			}
		})(ergebnisse);
	};

	ergebnisAbleiten = async () => {
		await api.call(async () => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return;
			}
			const result = await api.server.dupliziereGostBlockungMitErgebnis(api.schema, this.auswahlErgebnis.id);
			this.mapBlockungen.set(result.id, result);
			this.setPatchedState({ mapBlockungen: this.mapBlockungen });
			await this.gotoBlockung(result);
		})();
	};

	ergebnisHochschreiben = async () => {
		await api.call(async () => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return;
			}
			const abiturjahr = this.abiturjahr;
			const halbjahr = this.halbjahr.next()?.id ?? this.halbjahr.id;
			const result = await api.server.schreibeGostBlockungsErgebnisHoch(api.schema, this.auswahlErgebnis.id);
			await RouteManager.doRoute(routeGostKursplanung.getRouteBlockung(abiturjahr, halbjahr, result.id));
		})();
	};

	ergebnisAktivieren = async (): Promise<boolean> => {
		return await api.call(async (): Promise<boolean> => {
			if ((!this.hatBlockung) || (this._state.value.auswahlErgebnis === undefined)) {
				return false;
			}
			await api.server.activateGostBlockungsergebnis(api.schema, this.auswahlErgebnis.id);
			this.jahrgangsdaten.istBlockungFestgelegt[this.halbjahr.id] = true;
			this.auswahlBlockung.istAktiv = true;
			this.datenmanager.daten().istAktiv = true;
			this.ergebnismanager.getErgebnis().istAktiv = true;
			this.auswahlErgebnis.istAktiv = true;
			this.commit();
			return true;
		})();
	};

	revertBlockung = async () => {
		await api.call(async () => {
			await api.server.revertActivateGostBlockungsergebnis(api.schema, this.jahrgangsdaten.abiturjahr, this.halbjahr.id);
			this.jahrgangsdaten.istBlockungFestgelegt[this.halbjahr.id] = false;
			this.setPatchedState({ jahrgangsdaten: this.jahrgangsdaten });
		})();
	};

	ergebnisSynchronisieren = async (): Promise<void> => {
		await api.call(async (): Promise<void> => {
			if ((!this.hatBlockung && !this.jahrgangsdaten.istBlockungFestgelegt[this.halbjahr.id]) || (this._state.value.auswahlErgebnis === undefined)) {
				return;
			}
			await api.server.syncGostBlockungsergebnis(api.schema, this.auswahlErgebnis.id);
		})();
	};

	gotoHalbjahr = async (value: GostHalbjahr) => {
		await RouteManager.doRoute(routeGostKursplanung.getRouteHalbjahr(this.abiturjahr, value.id));
	};

	gotoBlockung = async (value: GostBlockungListeneintrag | undefined) => {
		if (value !== this._state.value.auswahlBlockung) {
			if (value === undefined) {
				await RouteManager.doRoute(routeGostKursplanung.getRouteHalbjahr(this.abiturjahr, this.halbjahr.id));
			} else {
				await RouteManager.doRoute(routeGostKursplanung.getRouteBlockung(this.abiturjahr, this.halbjahr.id, value.id));
			}
		}
	};

	gotoErgebnis = async (value: GostBlockungsergebnis | number | undefined) => {
		let id;
		if (value instanceof GostBlockungsergebnis) {
			id = value.id;
		} else {
			id = value;
		}
		if ((id !== this.auswahlErgebnis.id) && (!RouteManager.isActive())) {
			if (this.hatErgebnis && this.hatSchueler && (id !== undefined)) {
				await RouteManager.doRoute(routeGostKursplanung.getRouteSchueler(this.abiturjahr, this.halbjahr.id, this.auswahlBlockung.id, id, this.auswahlSchueler.id));
			} else if (id === undefined) {
				await RouteManager.doRoute(routeGostKursplanung.getRouteBlockung(this.abiturjahr, this.halbjahr.id, this.auswahlBlockung.id));
			} else {
				await RouteManager.doRoute(routeGostKursplanung.getRouteErgebnis(this.abiturjahr, this.halbjahr.id, this.auswahlBlockung.id, id));
			}
		}
	};

	gotoSchueler = async (schueler: Schueler) => {
		// TODO alle möglichen Fälle von fehlenden Informationen (Abiturjahr, Blockung und Ergebnis) berücksichtigen
		if ((!this.hatSchueler) || (schueler.id !== this.auswahlSchueler.id)) {
			const abiturjahr = this.abiturjahr;
			const halbjahr = this.halbjahr.id;
			const idblockung = this.auswahlBlockung.id;
			const idergebnis = this.auswahlErgebnis.id;
			const idschueler = schueler.id;
			await RouteManager.doRoute(routeGostKursplanungSchueler.getRoute({ abiturjahr, halbjahr, idblockung, idergebnis, idschueler }));
		}
	};

	public kurssortierung = computed<'fach' | 'kursart'>({
		get: () => {
			const value = configStateImpl.config.getValue('gost.kursplanung.kursansicht.sortierung');
			if (((value !== 'kursart') && (value !== 'fach'))) {
				return 'kursart';
			}
			return value;
		},
		set: (value) => {
			void configStateImpl.config.setValue('gost.kursplanung.kursansicht.sortierung', value);
			if (this._state.value.ergebnismanager !== undefined) {
				if (value === 'kursart') {
					this.ergebnismanager.kursSetSortierungKursartFachNummer();
				} else {
					this.ergebnismanager.kursSetSortierungFachKursartNummer();
				}
			}
			this.commit();
		},
	});

	protected getListeKursauswahl(): List<number> {
		const result = new ArrayList<number>();
		for (const idKurs of this.kursAuswahl) {
			result.add(idKurs);
		}
		return result;
	}

	regelnUpdate = async (update: GostBlockungRegelUpdate) => {
		await api.call(async (update: GostBlockungRegelUpdate) => {
			if (update.listEntfernen.isEmpty() && update.listHinzuzufuegen.isEmpty()) {
				return;
			}
			const listAdd = await api.server.updateGostBlockungRegeln(update, api.schema, this.auswahlBlockung.id);
			update.listHinzuzufuegen = listAdd;
			this.ergebnismanager.regelupdateExecute(update);
			this.commit();
		}, { name: 'gost.regelnUpdate' })(update);
	};

	setKursAuswahl = (kursauswahl: JavaSet<number>) => {
		this.setPatchedState({ kursauswahl });
	};
}
